/**
 * Timeline Code Templates - Pre-built editing patterns for common operations
 * 
 * This service provides template JavaScript code for common video editing patterns,
 * enabling fast, reliable timeline manipulation without AI generation delays.
 */

class TimelineCodeTemplates {
  constructor() {
    this.templates = new Map();
    this._initializeTemplates();
  }

  _initializeTemplates() {
    // Story Structure Templates
    this.templates.set('cookingStory', {
      name: 'Cooking Story Structure',
      description: 'Arranges clips in a logical cooking sequence',
      parameters: ['preserveOrder'],
      template: `
// Cooking story structure: Ingredients → Prep → Cooking → Result
const ingredientClips = timeline.findClipsByTag('ingredients');
const cookingClips = timeline.findClipsByTag('cooking');
const actionClips = timeline.findClipsByTag('action');
const reactionClips = timeline.findClipsByTag('reaction');

timeline.clear();
let currentTime = 0;

// Start with ingredients/setup
if (ingredientClips.length > 0) {
  timeline.addClip(ingredientClips[0], currentTime);
  currentTime += ingredientClips[0].duration;
  console.log('Added ingredients clip');
}

// Add cooking action clips
for (const clip of cookingClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Added cooking clip: \${clip.name}\`);
}

// Add general action clips if any
for (const clip of actionClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Added action clip: \${clip.name}\`);
}

// End with reaction/result
if (reactionClips.length > 0) {
  timeline.addClip(reactionClips[0], currentTime);
  console.log('Added reaction/result clip');
}

console.log('Cooking story structure applied successfully');
      `.trim()
    });

    this.templates.set('startEndPattern', {
      name: 'Start-End Pattern',
      description: 'Arranges timeline to start with specific content and end with specific content',
      parameters: ['startTag', 'endTag'],
      template: `
// Start with {startTag}, end with {endTag}
const startClips = timeline.findClipsByTag('{startTag}');
const endClips = timeline.findClipsByTag('{endTag}');

if (startClips.length === 0) {
  console.log('No clips found with tag: {startTag}');
  return;
}
if (endClips.length === 0) {
  console.log('No clips found with tag: {endTag}');
  return;
}

// Get all other clips
const allClips = timeline.getClips();
const otherClips = allClips.filter(clip => 
  !startClips.some(s => s.id === clip.id) && 
  !endClips.some(e => e.id === clip.id)
);

timeline.clear();
let currentTime = 0;

// Add start clip
timeline.addClip(startClips[0], currentTime);
currentTime += startClips[0].duration;
console.log(\`Started with: \${startClips[0].name}\`);

// Add middle clips
for (const clip of otherClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
}

// Add end clip
timeline.addClip(endClips[0], currentTime);
console.log(\`Ended with: \${endClips[0].name}\`);

console.log('Start-end pattern applied successfully');
      `.trim()
    });

    this.templates.set('cutToLength', {
      name: 'Cut to Specific Duration',
      description: 'Intelligently cuts timeline to target duration',
      parameters: ['targetDuration', 'priorityTags'],
      template: `
// Cut timeline to {targetDuration} seconds
const targetDuration = {targetDuration};
const priorityTags = {priorityTags};
const allClips = timeline.getClips();

// Score clips based on priority tags
const scoredClips = allClips.map(clip => {
  let score = 1; // Base score
  
  // Boost score for priority content
  if (clip.tags) {
    for (const tag of priorityTags) {
      if (clip.tags.includes(tag)) {
        score += 2;
      }
    }
  }
  
  return { clip, score };
});

// Sort by score (highest first)
scoredClips.sort((a, b) => b.score - a.score);

timeline.clear();
let currentTime = 0;
let includedClips = [];

// Add clips until we reach target duration
for (const { clip } of scoredClips) {
  if (currentTime + clip.duration <= targetDuration) {
    timeline.addClip(clip, currentTime);
    currentTime += clip.duration;
    includedClips.push(clip);
    console.log(\`Included: \${clip.name} (\${clip.duration}s)\`);
  } else if (targetDuration - currentTime > 1) {
    // Trim last clip to fit
    const remainingTime = targetDuration - currentTime;
    timeline.addClip(clip, currentTime);
    timeline.trimClip(clip.id, clip.startTime, clip.startTime + remainingTime);
    console.log(\`Trimmed and included: \${clip.name} (\${remainingTime}s)\`);
    break;
  }
}

console.log(\`Timeline cut to \${targetDuration}s. Final duration: \${timeline.getDuration()}s\`);
console.log(\`Included \${includedClips.length} clips\`);
      `.trim()
    });

    this.templates.set('removeContent', {
      name: 'Remove Content by Tag',
      description: 'Removes all clips with specified content tags',
      parameters: ['contentTags'],
      template: `
// Remove clips with specified content tags
const tagsToRemove = {contentTags};
let removedCount = 0;

for (const tag of tagsToRemove) {
  const clipsToRemove = timeline.findClipsByTag(tag);
  
  for (const clip of clipsToRemove) {
    timeline.removeClip(clip.id);
    removedCount++;
    console.log(\`Removed: \${clip.name} (tag: \${tag})\`);
  }
}

console.log(\`Removed \${removedCount} clips with tags: \${tagsToRemove.join(', ')}\`);
console.log(\`Remaining clips: \${timeline.getClips().length}\`);
console.log(\`New duration: \${timeline.getDuration()}s\`);
      `.trim()
    });

    this.templates.set('arrangeByEnergy', {
      name: 'Arrange by Energy Level',
      description: 'Arranges clips from low to high energy',
      parameters: ['buildToClimax'],
      template: `
// Arrange clips by energy level
const allClips = timeline.getClips();
const buildToClimax = {buildToClimax};

// Categorize clips by energy level
const lowEnergyClips = timeline.findClipsByTag('talking').concat(timeline.findClipsByTag('ingredients'));
const mediumEnergyClips = timeline.findClipsByTag('cooking').concat(timeline.findClipsByTag('prep'));
const highEnergyClips = timeline.findClipsByTag('action').concat(timeline.findClipsByTag('reaction'));

timeline.clear();
let currentTime = 0;

const clipSequence = buildToClimax 
  ? [...lowEnergyClips, ...mediumEnergyClips, ...highEnergyClips]
  : [...highEnergyClips, ...mediumEnergyClips, ...lowEnergyClips];

for (const clip of clipSequence) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Added: \${clip.name}\`);
}

console.log(\`Arranged \${clipSequence.length} clips by energy level\`);
console.log(buildToClimax ? 'Building to climax' : 'Starting with high energy');
      `.trim()
    });

    this.templates.set('createMontage', {
      name: 'Create Quick Montage',
      description: 'Creates a fast-paced montage from action clips',
      parameters: ['maxClipDuration', 'totalDuration'],
      template: `
// Create quick montage
const maxClipDuration = {maxClipDuration};
const totalDuration = {totalDuration};

// Get action and dynamic clips
const actionClips = timeline.findClipsByTag('action');
const cookingClips = timeline.findClipsByTag('cooking');
const reactionClips = timeline.findClipsByTag('reaction');

const montageClips = [...actionClips, ...cookingClips, ...reactionClips];

if (montageClips.length === 0) {
  console.log('No suitable clips found for montage');
  return;
}

timeline.clear();
let currentTime = 0;
let clipsAdded = 0;

for (const clip of montageClips) {
  if (currentTime >= totalDuration) break;
  
  // Trim clip to max duration
  const clipDuration = Math.min(clip.duration, maxClipDuration);
  const remainingTime = totalDuration - currentTime;
  const finalDuration = Math.min(clipDuration, remainingTime);
  
  timeline.addClip(clip, currentTime);
  if (finalDuration < clip.duration) {
    timeline.trimClip(clip.id, clip.startTime, clip.startTime + finalDuration);
  }
  
  currentTime += finalDuration;
  clipsAdded++;
  console.log(\`Added to montage: \${clip.name} (\${finalDuration}s)\`);
}

console.log(\`Created montage with \${clipsAdded} clips, duration: \${currentTime}s\`);
      `.trim()
    });

    this.templates.set('splitLongClips', {
      name: 'Split Long Clips',
      description: 'Splits clips longer than specified duration',
      parameters: ['maxDuration'],
      template: `
// Split clips longer than {maxDuration} seconds
const maxDuration = {maxDuration};
const clipsToSplit = timeline.getClips().filter(clip => clip.duration > maxDuration);

console.log(\`Found \${clipsToSplit.length} clips longer than \${maxDuration}s\`);

for (const clip of clipsToSplit) {
  const numSplits = Math.ceil(clip.duration / maxDuration);
  console.log(\`Splitting \${clip.name} into \${numSplits} parts\`);
  
  // Split the clip multiple times if needed
  let splitTime = maxDuration;
  while (splitTime < clip.duration - 1) { // Leave at least 1 second
    timeline.splitClip(clip.id, splitTime - clip.startTime);
    splitTime += maxDuration;
  }
}

console.log('Long clips have been split');
console.log(\`Timeline now has \${timeline.getClips().length} clips\`);
      `.trim()
    });

    this.templates.set('findBestMoments', {
      name: 'Find Best Moments',
      description: 'Selects clips with highest engagement potential',
      parameters: ['maxClips'],
      template: `
// Find and arrange best moments
const maxClips = {maxClips};
const allClips = timeline.getClips();

// Score clips based on content quality indicators
const scoredClips = allClips.map(clip => {
  let score = 0;
  
  if (clip.tags) {
    // Prefer clips with reaction or action
    if (clip.tags.includes('reaction')) score += 3;
    if (clip.tags.includes('action')) score += 2;
    if (clip.tags.includes('talking')) score += 1;
  }
  
  // Prefer medium-length clips (not too short, not too long)
  if (clip.duration >= 3 && clip.duration <= 10) score += 1;
  
  return { clip, score };
});

// Sort by score and take the best ones
const bestClips = scoredClips
  .sort((a, b) => b.score - a.score)
  .slice(0, maxClips)
  .map(item => item.clip);

timeline.clear();
let currentTime = 0;

for (const clip of bestClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Selected best moment: \${clip.name} (score: \${scoredClips.find(s => s.clip.id === clip.id).score})\`);
}

console.log(\`Selected \${bestClips.length} best moments\`);
console.log(\`Total duration: \${currentTime}s\`);
      `.trim()
    });

    this.templates.set('balanceContent', {
      name: 'Balance Content Types',
      description: 'Evenly distributes different content types',
      parameters: ['contentTypes'],
      template: `
// Balance content types evenly
const contentTypes = {contentTypes};
const contentGroups = {};

// Group clips by content type
for (const contentType of contentTypes) {
  contentGroups[contentType] = timeline.findClipsByTag(contentType);
}

// Find the minimum count to balance
const minCount = Math.min(...Object.values(contentGroups).map(group => group.length));

if (minCount === 0) {
  console.log('Cannot balance: some content types have no clips');
  return;
}

timeline.clear();
let currentTime = 0;

// Interleave clips from each content type
for (let i = 0; i < minCount; i++) {
  for (const contentType of contentTypes) {
    const clip = contentGroups[contentType][i];
    if (clip) {
      timeline.addClip(clip, currentTime);
      currentTime += clip.duration;
      console.log(\`Added \${contentType} clip: \${clip.name}\`);
    }
  }
}

console.log(\`Balanced \${contentTypes.length} content types with \${minCount} clips each\`);
      `.trim()
    });

    // Selected Clips Templates
    this.templates.set('selectedClipsSpeedUp', {
      name: 'Speed Up Selected Clips',
      description: 'Speeds up only the selected clips',
      parameters: ['selectedClipIds', 'speedMultiplier'],
      template: `
// Speed up selected clips
const selectedIds = {selectedClipIds};
const speedMultiplier = {speedMultiplier};

if (selectedIds.length === 0) {
  console.log('No clips selected');
  return;
}

for (const clipId of selectedIds) {
  const clip = timeline.getClips().find(c => c.id === clipId);
  if (clip) {
    // Simulate speed effect by trimming duration
    const newDuration = clip.duration / speedMultiplier;
    timeline.trimClip(clipId, clip.startTime, clip.startTime + newDuration);
    console.log(\`Sped up: \${clip.name} to \${speedMultiplier}x (\${newDuration.toFixed(1)}s)\`);
  }
}

console.log(\`Applied \${speedMultiplier}x speed to \${selectedIds.length} selected clips\`);
      `.trim()
    });

    this.templates.set('selectedClipsMove', {
      name: 'Move Selected Clips',
      description: 'Moves selected clips to a new position',
      parameters: ['selectedClipIds', 'targetPosition'],
      template: `
// Move selected clips to new position
const selectedIds = {selectedClipIds};
const targetPosition = {targetPosition}; // 'beginning', 'end', or time in seconds

if (selectedIds.length === 0) {
  console.log('No clips selected');
  return;
}

// Get selected clips and remove them temporarily
const selectedClips = [];
for (const clipId of selectedIds) {
  const clip = timeline.getClips().find(c => c.id === clipId);
  if (clip) {
    selectedClips.push(clip);
    timeline.removeClip(clipId);
  }
}

// Calculate target time
let targetTime = 0;
if (targetPosition === 'end') {
  targetTime = timeline.getDuration();
} else if (targetPosition === 'beginning') {
  targetTime = 0;
} else if (typeof targetPosition === 'number') {
  targetTime = targetPosition;
}

// Add clips at new position
let currentTime = targetTime;
for (const clip of selectedClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Moved: \${clip.name} to \${currentTime.toFixed(1)}s\`);
}

console.log(\`Moved \${selectedClips.length} clips to \${targetPosition}\`);
      `.trim()
    });

    this.templates.set('selectedClipsTransitions', {
      name: 'Add Transitions to Selected Clips',
      description: 'Adds transitions between selected clips',
      parameters: ['selectedClipIds', 'transitionType'],
      template: `
// Add transitions between selected clips
const selectedIds = {selectedClipIds};
const transitionType = '{transitionType}'; // 'crossfade', 'cut', 'dissolve'

if (selectedIds.length < 2) {
  console.log('Need at least 2 clips selected for transitions');
  return;
}

// Get selected clips in timeline order
const selectedClips = timeline.getClips()
  .filter(clip => selectedIds.includes(clip.id))
  .sort((a, b) => a.startTime - b.startTime);

let transitionsAdded = 0;

for (let i = 0; i < selectedClips.length - 1; i++) {
  const currentClip = selectedClips[i];
  const nextClip = selectedClips[i + 1];
  
  // Add transition effect (simulated by slight overlap)
  const transitionDuration = 0.5; // 0.5 second transition
  const newNextStart = Math.max(0, currentClip.startTime + currentClip.duration - transitionDuration);
  
  timeline.moveClip(nextClip.id, newNextStart);
  transitionsAdded++;
  
  console.log(\`Added \${transitionType} transition between \${currentClip.name} and \${nextClip.name}\`);
}

console.log(\`Added \${transitionsAdded} \${transitionType} transitions to selected clips\`);
      `.trim()
    });

    this.templates.set('selectedClipsArrange', {
      name: 'Arrange Selected Clips',
      description: 'Arranges selected clips by energy or content type',
      parameters: ['selectedClipIds', 'arrangeBy'],
      template: `
// Arrange selected clips
const selectedIds = {selectedClipIds};
const arrangeBy = '{arrangeBy}'; // 'energy', 'duration', 'name', 'tags'

if (selectedIds.length === 0) {
  console.log('No clips selected');
  return;
}

// Get selected clips
const selectedClips = timeline.getClips().filter(clip => selectedIds.includes(clip.id));
const startTime = Math.min(...selectedClips.map(clip => clip.startTime));

// Remove clips temporarily
for (const clipId of selectedIds) {
  timeline.removeClip(clipId);
}

// Sort clips based on arrangement type
let sortedClips;
switch (arrangeBy) {
  case 'energy':
    // Sort by energy level (talking < cooking < action)
    sortedClips = selectedClips.sort((a, b) => {
      const getEnergyLevel = (clip) => {
        if (clip.tags?.includes('talking')) return 1;
        if (clip.tags?.includes('cooking')) return 2;
        if (clip.tags?.includes('action')) return 3;
        return 2;
      };
      return getEnergyLevel(a) - getEnergyLevel(b);
    });
    break;
  case 'duration':
    sortedClips = selectedClips.sort((a, b) => a.duration - b.duration);
    break;
  case 'name':
    sortedClips = selectedClips.sort((a, b) => a.name.localeCompare(b.name));
    break;
  default:
    sortedClips = selectedClips;
}

// Re-add clips in sorted order
let currentTime = startTime;
for (const clip of sortedClips) {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
  console.log(\`Arranged: \${clip.name} at \${currentTime.toFixed(1)}s\`);
}

console.log(\`Arranged \${sortedClips.length} clips by \${arrangeBy}\`);
      `.trim()
    });

    this.templates.set('selectedClipsRemove', {
      name: 'Remove Selected Clips',
      description: 'Removes the selected clips from timeline',
      parameters: ['selectedClipIds'],
      template: `
// Remove selected clips
const selectedIds = {selectedClipIds};

if (selectedIds.length === 0) {
  console.log('No clips selected');
  return;
}

let removedCount = 0;
for (const clipId of selectedIds) {
  const clip = timeline.getClips().find(c => c.id === clipId);
  if (clip) {
    timeline.removeClip(clipId);
    removedCount++;
    console.log(\`Removed: \${clip.name}\`);
  }
}

console.log(\`Removed \${removedCount} selected clips\`);
console.log(\`Remaining clips: \${timeline.getClips().length}\`);
console.log(\`New timeline duration: \${timeline.getDuration()}s\`);
      `.trim()
    });
  }

  /**
   * Get template by name
   */
  getTemplate(templateName) {
    return this.templates.get(templateName);
  }

  /**
   * Get all available templates
   */
  getAllTemplates() {
    return Array.from(this.templates.entries()).map(([key, template]) => ({
      key,
      name: template.name,
      description: template.description,
      parameters: template.parameters
    }));
  }

  /**
   * Generate executable code from template with parameters
   */
  generateCode(templateName, parameters = {}) {
    const template = this.templates.get(templateName);
    if (!template) {
      throw new Error(`Template '${templateName}' not found`);
    }

    let code = template.template;

    // Replace parameter placeholders
    for (const [key, value] of Object.entries(parameters)) {
      const placeholder = `{${key}}`;
      let replacement;

      if (Array.isArray(value)) {
        replacement = JSON.stringify(value);
      } else if (typeof value === 'string') {
        replacement = value;
      } else {
        replacement = String(value);
      }

      code = code.replaceAll(placeholder, replacement);
    }

    return {
      code,
      template: template.name,
      description: template.description,
      estimatedExecutionTime: this._estimateExecutionTime(code),
      safetyScore: this._calculateSafetyScore(code)
    };
  }

  /**
   * Find matching templates for user request
   */
  findMatchingTemplates(userRequest, timelineContext = {}, options = {}) {
    const request = userRequest.toLowerCase();
    const matches = [];
    const hasSelectedClips = timelineContext.selectedClipIds && timelineContext.selectedClipIds.length > 0;

    // Pattern matching logic
    if (request.includes('start with') && request.includes('end with')) {
      matches.push({
        template: 'startEndPattern',
        confidence: 0.9,
        suggestedParameters: this._extractStartEndParameters(request)
      });
    }

    if (request.includes('cook') || request.includes('recipe') || request.includes('ingredient')) {
      matches.push({
        template: 'cookingStory',
        confidence: 0.8,
        suggestedParameters: { preserveOrder: true }
      });
    }

    if (request.match(/cut to \d+/) || request.match(/make it \d+ second/)) {
      const durationMatch = request.match(/(\d+)\s*second/);
      matches.push({
        template: 'cutToLength',
        confidence: 0.9,
        suggestedParameters: {
          targetDuration: durationMatch ? parseInt(durationMatch[1]) : 30,
          priorityTags: ['talking', 'action', 'reaction']
        }
      });
    }

    if (request.includes('remove') || request.includes('delete')) {
      matches.push({
        template: 'removeContent',
        confidence: 0.8,
        suggestedParameters: {
          contentTags: this._extractContentTags(request)
        }
      });
    }

    if (request.includes('montage') || request.includes('quick') || request.includes('fast-paced')) {
      matches.push({
        template: 'createMontage',
        confidence: 0.8,
        suggestedParameters: {
          maxClipDuration: 3,
          totalDuration: 30
        }
      });
    }

    if (request.includes('split') || request.includes('break')) {
      matches.push({
        template: 'splitLongClips',
        confidence: 0.7,
        suggestedParameters: {
          maxDuration: 10
        }
      });
    }

    if (request.includes('best') || request.includes('highlight')) {
      matches.push({
        template: 'findBestMoments',
        confidence: 0.8,
        suggestedParameters: {
          maxClips: 5
        }
      });
    }

    if (request.includes('balance') || request.includes('evenly')) {
      matches.push({
        template: 'balanceContent',
        confidence: 0.7,
        suggestedParameters: {
          contentTypes: ['talking', 'action', 'cooking']
        }
      });
    }

    // Selected clips patterns (only when clips are selected)
    if (hasSelectedClips) {
      const selectedClipIds = timelineContext.selectedClipIds;

      if (request.includes('speed') && (request.includes('up') || request.includes('2x') || request.includes('fast'))) {
        const speedMatch = request.match(/(\d+)x/) || request.match(/(\d+)\s*times/);
        const speedMultiplier = speedMatch ? parseInt(speedMatch[1]) : 2;
        
        matches.push({
          template: 'selectedClipsSpeedUp',
          confidence: 0.95,
          suggestedParameters: {
            selectedClipIds,
            speedMultiplier
          }
        });
      }

      if (request.includes('move') || request.includes('place')) {
        let targetPosition = 'beginning';
        if (request.includes('end') || request.includes('last')) {
          targetPosition = 'end';
        } else if (request.includes('beginning') || request.includes('start') || request.includes('first')) {
          targetPosition = 'beginning';
        } else if (request.includes('center') || request.includes('middle')) {
          targetPosition = 'center';
        }

        matches.push({
          template: 'selectedClipsMove',
          confidence: 0.9,
          suggestedParameters: {
            selectedClipIds,
            targetPosition
          }
        });
      }

      if (request.includes('transition') || request.includes('fade') || request.includes('blend')) {
        let transitionType = 'crossfade';
        if (request.includes('cut')) {
          transitionType = 'cut';
        } else if (request.includes('dissolve')) {
          transitionType = 'dissolve';
        }

        matches.push({
          template: 'selectedClipsTransitions',
          confidence: 0.85,
          suggestedParameters: {
            selectedClipIds,
            transitionType
          }
        });
      }

      if (request.includes('arrange') || request.includes('sort') || request.includes('order')) {
        let arrangeBy = 'energy';
        if (request.includes('duration') || request.includes('length')) {
          arrangeBy = 'duration';
        } else if (request.includes('name') || request.includes('alphabetical')) {
          arrangeBy = 'name';
        } else if (request.includes('energy') || request.includes('intensity')) {
          arrangeBy = 'energy';
        }

        matches.push({
          template: 'selectedClipsArrange',
          confidence: 0.8,
          suggestedParameters: {
            selectedClipIds,
            arrangeBy
          }
        });
      }

      if (request.includes('remove') || request.includes('delete')) {
        matches.push({
          template: 'selectedClipsRemove',
          confidence: 0.9,
          suggestedParameters: {
            selectedClipIds
          }
        });
      }

      // Boost confidence for selected clips operations when clips are selected
      if (options.mode === 'selected_clips') {
        matches.forEach(match => {
          if (match.template.startsWith('selectedClips')) {
            match.confidence = Math.min(match.confidence + 0.1, 1.0);
          }
        });
      }
    }

    return matches.sort((a, b) => b.confidence - a.confidence);
  }

  _extractStartEndParameters(request) {
    const startMatch = request.match(/start with\s+(\w+)/);
    const endMatch = request.match(/end with\s+(\w+)/);

    return {
      startTag: startMatch ? startMatch[1] : 'ingredients',
      endTag: endMatch ? endMatch[1] : 'reaction'
    };
  }

  _extractContentTags(request) {
    const commonTags = ['talking', 'action', 'cooking', 'reaction', 'ingredients'];
    const foundTags = commonTags.filter(tag => request.includes(tag));
    return foundTags.length > 0 ? foundTags : ['unwanted'];
  }

  _estimateExecutionTime(code) {
    // Simple heuristic based on code complexity
    const lines = code.split('\n').length;
    const loopCount = (code.match(/for\s*\(/g) || []).length;
    const apiCalls = (code.match(/timeline\./g) || []).length;

    return Math.min(0.1 + (lines * 0.01) + (loopCount * 0.05) + (apiCalls * 0.02), 2.0);
  }

  _calculateSafetyScore(code) {
    // All templates are pre-validated, so they're inherently safe
    const dangerousPatterns = ['eval', 'Function', 'import', 'require'];
    const hasDangerousPattern = dangerousPatterns.some(pattern => code.includes(pattern));
    
    return hasDangerousPattern ? 0.5 : 0.95;
  }
}

// Export singleton instance
const timelineCodeTemplates = new TimelineCodeTemplates();
export default timelineCodeTemplates;